import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, MessageCircle, ShieldCheck, Clock, MapPin, Wrench } from "lucide-react";
import { BUSINESS, telHref, waHref } from "../lib/business";

const HERO_SLIDES = [
    {
        src: "https://customer-assets.emergentagent.com/job_su-onarimi-now/artifacts/jly6vr3j_plumber-work-bathroom-plumbing-repair-service-assemble-plumber-work-bathroom-plumbing-repair-service-assemble-114657204.webp",
        alt: "Kayseri tesisatçı - banyo tesisatı kurulumu yapan su tesisatçısı",
    },
    {
        src: "https://customer-assets.emergentagent.com/job_su-onarimi-now/artifacts/z6awe4gt_m.webp",
        alt: "Kayseri su tesisatçısı - lavabo bataryası tamiri yapan tesisatçı",
    },
    {
        src: "https://customer-assets.emergentagent.com/job_su-onarimi-now/artifacts/b60u2ms3_68e3dd8bf160b062a2c93259_Plumbing%20Water%20Heater%20Repair%20%202560%20x%201440.webp",
        alt: "Kayseri kombi tamiri - şofben ve kombi onarımı yapan profesyonel tesisatçı",
    },
    {
        src: "https://customer-assets.emergentagent.com/job_su-onarimi-now/artifacts/xdqsng4y_images%20%281%29.jpg",
        alt: "Kayseri kombi montajı - su tesisatçısı tarafından yapılmış kombi kurulumu",
    },
];

export const Hero = () => {
    const [idx, setIdx] = useState(0);
    useEffect(() => {
        const t = setInterval(() => setIdx((p) => (p + 1) % HERO_SLIDES.length), 4000);
        return () => clearInterval(t);
    }, []);
    return (
        <section
            id="top"
            data-testid="hero-section"
            className="relative overflow-hidden bg-white"
        >
            <div className="absolute inset-0 grid-bg opacity-70 pointer-events-none" />
            <div className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full bg-[#0055FF]/10 blur-3xl pointer-events-none" />
            <div className="absolute -bottom-40 -left-20 w-[420px] h-[420px] rounded-full bg-[#25D366]/10 blur-3xl pointer-events-none" />

            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-16 lg:pt-20 lg:pb-24 grid lg:grid-cols-12 gap-10 items-center">
                <div className="lg:col-span-5">
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-slate-700 shadow-sm"
                    >
                        <span className="relative flex w-2 h-2">
                            <span className="absolute inline-flex w-full h-full rounded-full bg-[#25D366] opacity-75 animate-ping" />
                            <span className="relative inline-flex w-2 h-2 rounded-full bg-[#25D366]" />
                        </span>
                        Şu an aktif · {BUSINESS.city} içi 30 dk servis
                    </motion.div>

                    <motion.h1
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.05 }}
                        className="mt-5 font-black tracking-tighter leading-[0.95] text-slate-900 text-4xl sm:text-5xl lg:text-6xl"
                    >
                        Kayseri <span className="inline-block bg-gradient-to-r from-[#0055FF] to-[#3D7BFF] bg-clip-text text-transparent">Tesisatçı</span> <br className="hidden sm:block" />
                        7/24 <span className="inline-block bg-gradient-to-r from-[#0055FF] to-[#3D7BFF] bg-clip-text text-transparent">Su Tesisatçısı</span>
                    </motion.h1>

                    <motion.p
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.12 }}
                        className="mt-5 max-w-xl text-base sm:text-lg leading-relaxed text-slate-600"
                    >
                        Kayseri'de <strong className="text-slate-900">profesyonel tesisatçı</strong> arıyorsanız doğru yerdesiniz.
                        Tıkanıklık açma, su kaçağı tespiti, kombi & petek tamiri ve banyo-mutfak tesisatı
                        için <strong className="text-slate-900">7/24 su tesisatçısı</strong> hizmeti. Tek aramayla kapınızdayız —
                        temiz iş, hızlı geliş, uygun fiyat.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.18 }}
                        className="mt-8 flex flex-wrap items-center gap-3"
                    >
                        <a
                            href={telHref}
                            data-testid="hero-call-btn"
                            className="group relative inline-flex items-center gap-3 rounded-full bg-[#FF3B30] hover:bg-[#E6352B] text-white px-6 py-4 text-base font-bold shadow-[0_18px_40px_-14px_rgba(255,59,48,0.7)] transition-all hover:scale-[1.03] active:scale-95"
                        >
                            <span className="absolute inset-0 rounded-full bg-[#FF3B30] pulse-ring -z-10" />
                            <Phone className="w-5 h-5" strokeWidth={2.5} />
                            Hemen Ara · {BUSINESS.phonePretty}
                        </a>
                        <a
                            href={waHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="hero-whatsapp-btn"
                            className="inline-flex items-center gap-2 rounded-full bg-[#25D366] hover:bg-[#1EBE5D] text-white px-6 py-4 text-base font-bold shadow-[0_18px_40px_-14px_rgba(37,211,102,0.6)] transition-all hover:scale-[1.03] active:scale-95"
                        >
                            <MessageCircle className="w-5 h-5" strokeWidth={2.5} />
                            WhatsApp
                        </a>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.6, delay: 0.3 }}
                        className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-3"
                    >
                        {[
                            { Icon: Clock, label: "7/24 Hizmet" },
                            { Icon: ShieldCheck, label: "Garantili İşçilik" },
                            { Icon: Wrench, label: "Uzman Ekip" },
                            { Icon: MapPin, label: "Tüm Kayseri" },
                        ].map(({ Icon, label }) => (
                            <div
                                key={label}
                                className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2.5"
                            >
                                <Icon className="w-4 h-4 text-[#0055FF]" strokeWidth={2.5} />
                                <span className="text-sm font-semibold text-slate-800">{label}</span>
                            </div>
                        ))}
                    </motion.div>
                </div>

                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.7, delay: 0.1 }}
                    className="lg:col-span-7 relative"
                >
                    <div className="relative aspect-[4/3] max-w-3xl mx-auto">
                        <div className="absolute inset-0 rounded-[2.5rem] bg-gradient-to-br from-[#0055FF] to-[#0040CC] shadow-[0_30px_80px_-30px_rgba(0,85,255,0.6)]" />
                        <div className="absolute inset-3 rounded-[2rem] bg-white/10 backdrop-blur-sm border border-white/20 overflow-hidden">
                            <AnimatePresence mode="wait">
                                <motion.img
                                    key={idx}
                                    src={HERO_SLIDES[idx].src}
                                    alt={HERO_SLIDES[idx].alt}
                                    title={HERO_SLIDES[idx].alt}
                                    data-testid={`hero-slide-${idx}`}
                                    initial={{ opacity: 0, scale: 1.05 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.97 }}
                                    transition={{ duration: 0.8, ease: "easeOut" }}
                                    className="absolute inset-0 w-full h-full object-cover"
                                    loading="eager"
                                />
                            </AnimatePresence>

                            {/* SEO: hidden but crawlable alt list */}
                            <ul className="sr-only">
                                {HERO_SLIDES.map((s, i) => (
                                    <li key={i}>{s.alt}</li>
                                ))}
                            </ul>

                            {/* Slide indicators */}
                            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 z-10">
                                {HERO_SLIDES.map((_, i) => (
                                    <button
                                        key={i}
                                        onClick={() => setIdx(i)}
                                        data-testid={`hero-slide-dot-${i}`}
                                        aria-label={`Slayt ${i + 1}`}
                                        className={`h-1.5 rounded-full transition-all ${i === idx ? "w-8 bg-white" : "w-2 bg-white/50 hover:bg-white/80"}`}
                                    />
                                ))}
                            </div>
                        </div>

                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.4 }}
                            className="absolute -left-4 sm:-left-8 top-8 rounded-2xl bg-white border border-slate-200 shadow-xl px-4 py-3 flex items-center gap-3"
                        >
                            <div className="w-10 h-10 rounded-xl bg-[#E5EDFF] flex items-center justify-center">
                                <Clock className="w-5 h-5 text-[#0055FF]" strokeWidth={2.5} />
                            </div>
                            <div>
                                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Ort. Geliş</div>
                                <div className="text-base font-extrabold text-slate-900">30 Dakika</div>
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.55 }}
                            className="absolute -right-4 sm:-right-8 bottom-10 rounded-2xl bg-white border border-slate-200 shadow-xl px-4 py-3 flex items-center gap-3"
                        >
                            <div className="w-10 h-10 rounded-xl bg-[#DCFCE7] flex items-center justify-center">
                                <ShieldCheck className="w-5 h-5 text-[#16A34A]" strokeWidth={2.5} />
                            </div>
                            <div>
                                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Garanti</div>
                                <div className="text-base font-extrabold text-slate-900">İşçilik Garantili</div>
                            </div>
                        </motion.div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

export default Hero;
